import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { designSystem, commonStyles } from '../../../styles/design-system';

const MapContainer = styled(motion.div)`
  ${commonStyles.card}
  margin-bottom: ${designSystem.spacing.xl};
  position: relative;
  overflow: hidden;

  h3 {
    color: ${designSystem.colors.text.primary};
    font-family: ${designSystem.typography.fontFamily.secondary};
    font-size: ${designSystem.typography.fontSize.xl};
    margin-bottom: ${designSystem.spacing.lg};
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${designSystem.spacing.sm};
  }

  .map-wrapper {
    position: relative;
    height: 400px;
    background: ${designSystem.colors.background.secondary};
    border-radius: ${designSystem.borderRadius.lg};
    border: 2px solid ${designSystem.colors.border.light};
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: ${designSystem.spacing.md};
    
    .map-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(45deg, 
        ${designSystem.colors.background.secondary} 25%, 
        ${designSystem.colors.background.tertiary} 25%, 
        ${designSystem.colors.background.tertiary} 50%, 
        ${designSystem.colors.background.secondary} 50%, 
        ${designSystem.colors.background.secondary} 75%, 
        ${designSystem.colors.background.tertiary} 75%);
      background-size: 20px 20px;
      position: relative;
      
      .map-content {
        background: ${designSystem.colors.background.primary};
        padding: ${designSystem.spacing.xl};
        border-radius: ${designSystem.borderRadius.xl};
        text-align: center;
        border: 1px solid ${designSystem.colors.border.light};
        box-shadow: ${designSystem.shadows.lg};
        
        .icon {
          font-size: ${designSystem.typography.fontSize['5xl']};
          margin-bottom: ${designSystem.spacing.md};
        }
        
        h4 {
          color: ${designSystem.colors.text.primary};
          margin-bottom: ${designSystem.spacing.sm};
        }
        
        p {
          color: ${designSystem.colors.text.secondary};
          font-size: ${designSystem.typography.fontSize.sm};
        }
      }
    }
  }

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    .map-wrapper {
      height: 300px;
    }
  }
`;

const HeatmapLegend = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${designSystem.spacing.lg};
  padding: ${designSystem.spacing.md};
  background: ${designSystem.colors.background.secondary};
  border-radius: ${designSystem.borderRadius.lg};
  border: 1px solid ${designSystem.colors.border.light};

  .legend-item {
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.sm};
    
    .color-box {
      width: 20px;
      height: 20px;
      border-radius: 4px;
      border: 1px solid ${designSystem.colors.border.light};
    }
    
    .label {
      color: ${designSystem.colors.text.secondary};
      font-size: ${designSystem.typography.fontSize.sm};
    }
  }
`;

const LocationStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${designSystem.spacing.md};
  margin-top: ${designSystem.spacing.lg};
`;

const LocationCard = styled.div`
  background: ${designSystem.colors.background.tertiary};
  padding: ${designSystem.spacing.md};
  border-radius: ${designSystem.borderRadius.lg};
  border: 1px solid ${designSystem.colors.border.light};
  
  .location-header {
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.sm};
    margin-bottom: ${designSystem.spacing.sm};
    
    .flag {
      font-size: ${designSystem.typography.fontSize.lg};
    }
    
    .name {
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.semibold};
      font-size: ${designSystem.typography.fontSize.sm};
    }
  }
  
  .stats {
    display: flex;
    justify-content: space-between;
    
    .stat {
      text-align: center;
      
      .value {
        color: ${designSystem.colors.primary.main};
        font-weight: ${designSystem.typography.fontWeight.bold};
        font-size: ${designSystem.typography.fontSize.lg};
      }
      
      .label {
        color: ${designSystem.colors.text.secondary};
        font-size: ${designSystem.typography.fontSize.xs};
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    }
  }
`;

const InteractiveFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${designSystem.spacing.sm};
  margin-top: ${designSystem.spacing.lg};
  justify-content: center;
`;

const FeatureButton = styled.button<{ active?: boolean }>`
  background: ${props => props.active 
    ? designSystem.colors.primary.main
    : 'transparent'};
  color: ${props => props.active 
    ? '#000000' 
    : designSystem.colors.text.secondary};
  border: 2px solid ${designSystem.colors.primary.main};
  padding: ${designSystem.spacing.sm} ${designSystem.spacing.md};
  border-radius: ${designSystem.borderRadius.lg};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  font-size: ${designSystem.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${designSystem.transitions.normal};
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.xs};
  
  &:hover {
    background: ${designSystem.colors.primary.main};
    color: #000000;
    transform: translateY(-2px);
    box-shadow: ${designSystem.shadows.hover};
  }
`;

interface BookingLocation {
  country: string;
  city: string;
  flag: string;
  bookings: number;
  revenue: number;
  averageStay: number;
  coordinates: [number, number];
}

interface BookingMapProps {
  locations: BookingLocation[];
  totalBookings: number;
}

const BookingMap: React.FC<BookingMapProps> = ({ locations, totalBookings }) => {
  const [activeFeature, setActiveFeature] = useState<'bookings' | 'revenue' | 'stay'>('bookings');

  // Simuliamo dati di mappa interattiva
  const topLocations = locations
    .sort((a, b) => {
      switch (activeFeature) {
        case 'revenue':
          return b.revenue - a.revenue;
        case 'stay':
          return b.averageStay - a.averageStay;
        default:
          return b.bookings - a.bookings;
      }
    })
    .slice(0, 6);

  const getIntensityColor = (value: number, maxValue: number) => {
    const intensity = value / maxValue;
    if (intensity > 0.8) return designSystem.colors.status.error;
    if (intensity > 0.6) return '#ff6b35';
    if (intensity > 0.4) return designSystem.colors.status.warning;
    if (intensity > 0.2) return designSystem.colors.primary.main;
    return designSystem.colors.secondary.light;
  };

  return (
    <MapContainer
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <h3>🗺️ Mappa Prenotazioni Globale</h3>
      
      <div className="map-wrapper">
        <div className="map-placeholder">
          <div className="map-content">
            <div className="icon">🌍</div>
            <h4>Mappa Interattiva</h4>
            <p>Integrazione con servizi di mappa in sviluppo</p>
            <p>Visualizzazione heatmap delle {totalBookings} prenotazioni</p>
          </div>
        </div>
      </div>

      <InteractiveFeatures>
        <FeatureButton
          active={activeFeature === 'bookings'}
          onClick={() => setActiveFeature('bookings')}
        >
          📊 Prenotazioni
        </FeatureButton>
        <FeatureButton
          active={activeFeature === 'revenue'}
          onClick={() => setActiveFeature('revenue')}
        >
          💰 Revenue
        </FeatureButton>
        <FeatureButton
          active={activeFeature === 'stay'}
          onClick={() => setActiveFeature('stay')}
        >
          🏠 Durata Soggiorno
        </FeatureButton>
      </InteractiveFeatures>

      <HeatmapLegend>
        <div className="legend-item">
          <div 
            className="color-box" 
            style={{ backgroundColor: designSystem.colors.secondary.light }}
          />
          <span className="label">Bassa attività</span>
        </div>
        <div className="legend-item">
          <div 
            className="color-box" 
            style={{ backgroundColor: designSystem.colors.primary.main }}
          />
          <span className="label">Media attività</span>
        </div>
        <div className="legend-item">
          <div 
            className="color-box" 
            style={{ backgroundColor: designSystem.colors.status.warning }}
          />
          <span className="label">Alta attività</span>
        </div>
        <div className="legend-item">
          <div 
            className="color-box" 
            style={{ backgroundColor: designSystem.colors.status.error }}
          />
          <span className="label">Massima attività</span>
        </div>
      </HeatmapLegend>

      <LocationStats>
        {topLocations.map((location, index) => {
          const maxValue = Math.max(...locations.map(l => {
            switch (activeFeature) {
              case 'revenue': return l.revenue;
              case 'stay': return l.averageStay;
              default: return l.bookings;
            }
          }));

          const currentValue = activeFeature === 'revenue' 
            ? location.revenue 
            : activeFeature === 'stay' 
            ? location.averageStay 
            : location.bookings;

          return (
            <LocationCard key={`${location.country}-${location.city}`}>
              <div className="location-header">
                <span className="flag">{location.flag}</span>
                <span className="name">{location.city}, {location.country}</span>
              </div>
              <div className="stats">
                <div className="stat">
                  <div className="value">{location.bookings}</div>
                  <div className="label">Prenotazioni</div>
                </div>
                <div className="stat">
                  <div className="value">€{location.revenue.toLocaleString()}</div>
                  <div className="label">Revenue</div>
                </div>
                <div className="stat">
                  <div className="value">{location.averageStay.toFixed(1)}</div>
                  <div className="label">Notti medie</div>
                </div>
              </div>
            </LocationCard>
          );
        })}
      </LocationStats>
    </MapContainer>
  );
};

export default BookingMap; 